import { FetchResult } from '@apollo/client';

import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { useRequiredHouseholdId } from '@/modules/household/contexts/useRequiredHouseholdId';
import { diagnostics } from '@/utils/diagnostics';

import { mapFormDataToInput } from '../CreateEstateWaterfallModal.utils';
import { EstateWaterfallFormProps } from '../EstateWaterfallForm';
import {
  CreateEstateWaterfallVisualizationFilterMutation,
  DeleteEstateWaterfallFilterMutation,
  QueryEstateWaterfallModalDocument,
  useCreateEstateWaterfallMutation,
  useCreateEstateWaterfallVisualizationFilterMutation,
  useDuplicateEstateWaterfallMutation,
} from '../graphql/EstateWaterfallModal.generated';
import { EstateWaterfallFormData } from '../types';

interface UseCreateEstateWaterfallInput {
  isTwoGrantor: boolean;
  sourceEstateWaterfalls?: EstateWaterfallFormProps['sourceEstateWaterfalls'];
  isDuplicate?: boolean;
}

export function useCreateEstateWaterfall({
  isTwoGrantor,
  sourceEstateWaterfalls,
}: UseCreateEstateWaterfallInput) {
  const householdId = useRequiredHouseholdId();
  const { showFeedback } = useFeedback();
  const { setError } = useFormContext<EstateWaterfallFormData>();

  const [createWaterfallMutation, { data: createEstateWaterfallData }] =
    useCreateEstateWaterfallMutation({
      refetchQueries: [
        {
          query: QueryEstateWaterfallModalDocument,
          variables: { where: { id: householdId } },
        },
      ],
      awaitRefetchQueries: true,
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError);
        diagnostics.error(`Could not create estate waterfall`, error);
      },
    });

  const [createVizFilter] = useCreateEstateWaterfallVisualizationFilterMutation(
    {
      refetchQueries: 'active',
      awaitRefetchQueries: true,
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError);
        diagnostics.error(
          `Could not create estate waterfall viz filter`,
          error
        );
      },
    }
  );

  const [duplicateMutation, { data: duplicateEstateWaterfallData }] =
    useDuplicateEstateWaterfallMutation({
      refetchQueries: 'active',
      awaitRefetchQueries: true,
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError);
        diagnostics.error(`Could not duplicate estate waterfall`, error);
      },
    });

  const submitFailure = (message: string) => {
    diagnostics.error(message);
    // Sets isSubmitSuccessful to false
    setError('root', {
      type: 'manual',
      message,
    });
    // Sets isSubmitting to false
    return Promise.resolve();
  };

  const createWaterfall: (
    formData: EstateWaterfallFormData
  ) => Promise<
    | void
    | FetchResult<CreateEstateWaterfallVisualizationFilterMutation>
    | FetchResult<DeleteEstateWaterfallFilterMutation>
  > = async (formData) => {
    const shouldCreateFilter = formData.waterfallFilter;
    try {
      let waterfallID: string | undefined;

      // We are duplicating the waterfall if the formData includes a parentID to duplicate
      if (formData.parentID) {
        const makeHypothetical = formData._isHypothetical;
        const { data } = await duplicateMutation({
          variables: {
            input: {
              id: formData.parentID,
              newName: formData.displayName,
              makeHypothetical,
            },
            opts: {
              applyAutoGrouping: formData.shouldAutoGroup,
            },
          },
        });

        waterfallID = data?.duplicateEstateWaterfall.id;

        if (!waterfallID) {
          return submitFailure('Failed to duplicate waterfall, no waterfallID');
        }

        const { includedClientIDs, includedEntityIDs } =
          formData.waterfallFilter ?? {};

        if (!shouldCreateFilter) {
          return Promise.resolve();
        }

        return createVizFilter({
          variables: {
            input: {
              create: {
                householdID: householdId,
                includedClientIDs,
                includedEntityIDs,
                waterfallID,
              },
            },
          },
        });
      } else {
        const { data } = await createWaterfallMutation({
          variables: {
            input: {
              create: mapFormDataToInput({
                formData,
                isTwoGrantor,
                sourceEstateWaterfalls,
                householdId,
              }),
            },
            opts: {
              applyAutoGrouping: formData.shouldAutoGroup,
            },
          },
        });

        waterfallID = data?.createEstateWaterfall.id;
        if (!waterfallID) {
          return submitFailure('Failed to create waterfall, no waterfallID');
        }
      }

      if (!shouldCreateFilter) {
        return Promise.resolve();
      }

      const { includedClientIDs, includedEntityIDs } =
        formData.waterfallFilter ?? {};

      return createVizFilter({
        variables: {
          input: {
            create: {
              householdID: householdId,
              includedClientIDs,
              includedEntityIDs,
              waterfallID,
            },
          },
        },
      });
    } catch (e) {
      return submitFailure('Failed to create waterfall');
    }
  };

  return {
    createWaterfall,
    createEstateWaterfallData,
    duplicateEstateWaterfallData,
  };
}
